import { useEffect, lazy, Suspense, useMemo, useCallback } from 'react';
import { useSRZId } from '@/hooks';
import { AutoTooltip, LoadingSpinner } from '@/components';
import { Route, Switch, useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { UserFeedbackIcon } from '@/components/Icons';
import { useFeedbackEnabled } from '@/features/feedback/hooks/useFeedbackEnabled';
import clsx from 'clsx';
import { DetailsPanelWrapper } from '@/components/DetailsPanel/DetailsPanelWrapper';
import { isDev } from '@/constants';
import { Tabs, theme } from 'antd';
import { PageLayoutMainContent } from '@/components/PageLayout';
import { gql } from '@apollo/client';
import {
  useMainboardRoomsQuery,
  useMainboardSpaceQuery,
  useMainboardZonesQuery,
  useMainboardBuildingQuery,
} from '@/.generated/graphql';
import toast from 'react-hot-toast';
import { formatOperationError } from '@/api/formatError';
import { useFeatureGate } from '@statsig/react-bindings';
import TimeDisplay from '@/components/TimeDisplay';
import FloorSetupCard from '@/pages/LivePage/Floor/FloorSetupCard';
import RoomSetupCard from '@/pages/LivePage/Room/RoomSetupCard';

const DeviceManagementPage = lazy(() => import('@/pages/DeviceManagementPage'));
const Dashboard = lazy(() => import('../Dashboard'));
const ScopingToolPage = lazy(() => import('@/pages/StudioPage'));
const DemoPage = lazy(() => import('@/pages/DemoPage'));
const LivePageContainer = lazy(() => import('@/pages/LivePage/LivePageContainer'));

interface IMainboardProps {
  isRoom: boolean;
  isZone: boolean;
}

const TAB_ROUTES: Record<string, string> = {
  live: '/live',
  historical: '/',
  studio: '/studio',
  devices: '/devices',
  demo: '/demo',
};

// FIXME: this component is a redundant abstraction with OrganizationPage,
// the only component which renders it. Pieces should be pulled out into
// sub-components and bundled up into OrganizationPage.
export const Mainboard = ({ isRoom, isZone }: IMainboardProps) => {
  const { token } = theme.useToken();
  const { search, pathname } = useLocation();
  const { spaceId, roomId, zoneId, buildingId } = useSRZId();
  const { value: livePageEnabled } = useFeatureGate('live_page');
  const newFeedbackEnabled = useFeedbackEnabled();

  const { error: buildingErrorData } = useMainboardBuildingQuery({
    variables: {
      // asserted by skip below

      buildingId: buildingId!,
    },
    skip: !buildingId,
  });
  const { data: spaceData, error: spaceErrorData } = useMainboardSpaceQuery({
    variables: {
      // asserted by skip below

      spaceId: spaceId!,
    },
    skip: !spaceId,
  });
  const space = spaceData?.floors.data?.[0] ?? null;

  const { data: roomData, error: roomErrorData } = useMainboardRoomsQuery({
    variables: {
      // asserted by skip below

      roomId: roomId!,
    },
    skip: !spaceId || !roomId,
  });
  const room = roomData?.rooms?.data?.[0] ?? null;

  const { data: zoneData, error: zoneErrorData } = useMainboardZonesQuery({
    variables: {
      // asserted by skip below

      zoneId: zoneId!,
    },
    skip: !spaceId || !zoneId,
  });
  const zone = zoneData?.zones?.data?.[0] ?? null;

  const { t } = useTranslation();

  const renderError = () => {
    const errorEntries: [string, typeof buildingErrorData][] = [
      ['load building', buildingErrorData],
      ['load space', spaceErrorData],
      ['load room', roomErrorData],
      ['load zone', zoneErrorData],
    ];
    for (const [operation, errorData] of errorEntries) {
      if (errorData) {
        return formatOperationError(operation, errorData);
      }
    }
    return 'Failed to load data';
  };

  const hasData = space || room || zone;
  const hasError = !!spaceErrorData || !!roomErrorData || !!zoneErrorData;
  // if there's data available, but there's an error, show a toast instead
  // of blocking the whole view. If there's no data, we render a full
  // error page below.
  const toastErrorMessage = hasError && hasData ? renderError() : null;
  useEffect(() => {
    if (toastErrorMessage) {
      toast.error(toastErrorMessage, {
        id: 'dashboard-error',
      });
    }
  }, [toastErrorMessage]);
  const history = useHistory();

  const activeTabKey = useMemo(() => {
    if (pathname === '/live' || pathname.startsWith('/live/')) return 'live';
    if (pathname === '/studio' || pathname.startsWith('/studio/')) return 'studio';
    if (pathname === '/devices' || pathname.startsWith('/devices/')) return 'devices';
    if (pathname === '/demo' || pathname.startsWith('/demo/')) return 'demo';
    return 'historical';
  }, [pathname]);

  const tabItems = useMemo(() => {
    const items: { key: string; label: string }[] = [];
    if (livePageEnabled && spaceId) {
      items.push({ key: 'live', label: t('live') });
    }
    items.push({ key: 'historical', label: 'Historical' });
    if (!buildingId) {
      items.push({ key: 'studio', label: 'Studio' });
    }
    items.push({ key: 'devices', label: t('deviceManagement.title') });
    if (isDev) {
      items.push({ key: 'demo', label: 'Demo' });
    }
    return items;
  }, [livePageEnabled, spaceId, buildingId, t]);

  const handleTabChange = useCallback(
    (key: string) => {
      history.push((TAB_ROUTES[key] || '/') + search);
    },
    [history, search],
  );
  return (
    <div className={clsx('flex flex-grow flex-col min-h-0')}>
      {!hasData && hasError ? (
        <div className="flex h-full w-full items-center justify-center text-xl">
          {renderError()}
        </div>
      ) : (
        <>
          {/* Sensor setup: rendered under the floor title, above tabs — visible across all tabs */}
          {spaceId && !roomId && !zoneId && !buildingId && <FloorSetupCard spaceId={spaceId} />}
          {spaceId && roomId && !zoneId && !buildingId && (
            <RoomSetupCard roomId={roomId} spaceId={spaceId} />
          )}
          <div className="px-6 bg-surface">
            <Tabs
              activeKey={activeTabKey}
              onChange={handleTabChange}
              items={tabItems}
              tabBarExtraContent={{
                right: (
                  <span
                    style={{
                      fontSize: token.fontSizeSM,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    <span style={{ color: token.colorTextTertiary, marginRight: 4 }}>
                      Local Time:
                    </span>
                    <TimeDisplay style={{ color: token.colorText }} />
                  </span>
                ),
              }}
              style={{ marginBottom: 0 }}
              tabBarStyle={{ marginBottom: 0 }}
            />
          </div>
          <PageLayoutMainContent>
            <Suspense fallback={<LoadingSpinner />}>
              <Switch>
                <Route path="/studio">
                  <ScopingToolPage />
                </Route>

                <Route path="/devices">
                  <DeviceManagementPage />
                </Route>
                <Route path="/demo">
                  <DemoPage />
                </Route>
                <Route path="/live">
                  <LivePageContainer
                    // Force a remount when we change space/room/zone. This helps prevent caching of data across routes.
                    // At some point, we should probably use a router that supports this natively.
                    key={[buildingId, spaceId, roomId, zoneId].filter(Boolean).join('')}
                  />
                </Route>
                <Route path="/">
                  {(spaceId || buildingId) && (
                    <Dashboard
                      // Force a remount when we change space/room/zone. This helps prevent caching of data across routes.
                      // At some point, we should probably use a router that supports this natively.
                      key={[buildingId, spaceId, roomId, zoneId].filter(Boolean).join('')}
                      isRoom={isRoom}
                      isZone={isZone}
                      spaceId={spaceId}
                      buildingId={buildingId}
                    />
                  )}
                </Route>
              </Switch>
              <DetailsPanelWrapper isRoom={isRoom} isZone={isZone} />
            </Suspense>
          </PageLayoutMainContent>
        </>
      )}
      {!newFeedbackEnabled && (
        <AutoTooltip
          label="Share your feedback"
          className="absolute right-5 bottom-5 z-50"
          placement="bottom-end"
        >
          <a
            href="https://support.butlr.io/hc/en-us/requests/new"
            target="_blank"
            rel="noreferrer"
            aria-label="User feedback"
          >
            <UserFeedbackIcon className="text-content-primary hover:text-content-secondary" />
          </a>
        </AutoTooltip>
      )}
    </div>
  );
};

Mainboard.queries = {
  Building: gql`
    query MainboardBuilding($buildingId: ID!) {
      building(id: $buildingId) {
        id
        name
      }
    }
  `,
  Space: gql`
    query MainboardSpace($spaceId: String!) {
      floors(ids: [$spaceId]) {
        data {
          id: floor_id
          name
        }
      }
    }
  `,
  Room: gql`
    query MainboardRooms($roomId: String!) {
      rooms(ids: [$roomId]) {
        data {
          id: room_id
          name
        }
      }
    }
  `,
  Zone: gql`
    query MainboardZones($zoneId: String!) {
      zones(ids: [$zoneId]) {
        data {
          id: zone_id
          name
        }
      }
    }
  `,
};
